import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  SELECTED_TAB_KEY,
  USERTYPES,
} from '../../constants/Constants';
import { TOAST_TYPE } from '../../constants/Constants';
import { Tab, TabList } from '@fluentui/react-tabs';
import { PrimaryButton } from '@fluentui/react';

import { t } from 'i18next';
import userContext from '../../context/users/UserContext';
import themeContext from '../../context/theme/ThemeContext';
import toastContext from '../../context/toast/ToastContext';
import PacsQueueTab from './PacsQueueTab';
import PacsQueueList from './PacsQueueList';
import CustomBreadCrumb from '../../components/CustomBreadCrumb';
import { breadCrumbItems, tabDetails } from './Helper';
import PacsQueueExportCsvModal from './components/ExportCsv/PacsQueueExportCsvModal';
import { pacsQueueExportCsvFetchProgress, pacsQueueExportCsvDownloadFile } from '../../service/PacsQueueService';

const PacsQueue = () => {
  const { roles, username, loadByUsername, authenticated } =
    useContext(userContext);
  const { getThemeColor } = useContext(themeContext);
  const [selectedValue, setSelectedValue] = useState(
    SELECTED_TAB_KEY.PACSQUEUE + '1'
  );

  const { showToastv2, updateProgress, hideProgress } = useContext(toastContext);

  const [showPacsQueueExportCsvModal, setShowPacsQueueExportCsvModal] = useState(false);
  const [pacsQueueExportProgress, setPacsQueueExportProgress] = useState(0);
  const [pacsQueueExportProgressId, setPacsQueueExportProgressId] = useState(0);
  const [pacsQueueExportToken, setPacsQueueExportToken] = useState("");
  const [pacsQueueExportFetchInterval] = useState(2000);
  const pacsQueueExportPollIntervalRef = useRef(null);

  const handleExportInitiated = (progressId, token) => {
    setPacsQueueExportProgressId(progressId);
    setPacsQueueExportToken(token);
    setPacsQueueExportProgress(1);
  };

  const getFileName = (response) => {
    var disposition = response.headers["content-disposition"];
    var filename = "pacs_export.csv";
    if (disposition && disposition.indexOf("attachment") !== -1) {
      var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      var matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, "");
      }
    }
    return filename;
  };

  const downloadPacsQueueExportFile = async (token) => {
    const response = await pacsQueueExportCsvDownloadFile(token);
    if (!response) {
      showToastv2(TOAST_TYPE.ERROR, "Error while downloading report.");
      return;
    }
    if (response.status === 200) {
      const file = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", getFileName(response));
      document.body.appendChild(link);
      link.click();
      showToastv2(TOAST_TYPE.SUCCESS, "Export CSV downloaded");
      setPacsQueueExportProgress(101);
      link.remove();
    } else {
      showToastv2(TOAST_TYPE.ERROR, "Error while downloading report.");
    }
  };

  const fetchPacsQueueExportProgress = async (interval, token) => {
    const response = await pacsQueueExportCsvFetchProgress(token);
    if (!response || !response.data || !response.data.data) {
      clearInterval(interval);
      showToastv2(TOAST_TYPE.ERROR, "Failed to fetch export progress.");
      return;
    }
    if (response.status === 200) {
      if (response.data.data.status === "FAILED") {
        hideProgress(pacsQueueExportProgressId);
        setPacsQueueExportProgress(0);
        showToastv2(
          TOAST_TYPE.ERROR,
          response.data.data.error
            ? response.data.data.error
            : "Failed to download file."
        );
        clearInterval(interval);
        return;
      }
      setPacsQueueExportProgress(response.data.data.progress);
    }
    return response.data.data.progress;
  };

  useEffect(() => {
    if (pacsQueueExportProgress === 0) return;

    if (pacsQueueExportProgress < 100) {
      updateProgress(pacsQueueExportProgressId, pacsQueueExportProgress);
      const oldProgress = pacsQueueExportProgress;
      const currentToken = pacsQueueExportToken;
      let progress;
      const interval = setInterval(async () => {
        progress = await fetchPacsQueueExportProgress(interval, currentToken);
        if (progress > oldProgress) {
          clearInterval(interval);
        }
      }, pacsQueueExportFetchInterval);
      pacsQueueExportPollIntervalRef.current = interval;
    }

    if (pacsQueueExportProgress === 100) {
      updateProgress(pacsQueueExportProgressId, pacsQueueExportProgress);
      downloadPacsQueueExportFile(pacsQueueExportToken);
    }

    if (pacsQueueExportProgress === 101) {
      updateProgress(pacsQueueExportProgressId, 100);
      setTimeout(() => {
        hideProgress(pacsQueueExportProgressId);
        setPacsQueueExportProgress(0);
      }, 3000);
    }

    return () => {
      if (pacsQueueExportPollIntervalRef.current) {
        clearInterval(pacsQueueExportPollIntervalRef.current);
      }
    };
  }, [pacsQueueExportProgress, pacsQueueExportToken]);

  useEffect(() => {
    if (!authenticated) {
      console.log('--- Authentication again ----');
      loadByUsername();
      return;
    }
  }, [authenticated]);

  
  const onTabSelect = (event, data) => {
    let currentTab = data.value;
    console.log('currentTab->', currentTab);
    document.getElementById(currentTab).style.border = getThemeColor('border');
    document.getElementById(currentTab).style.borderBottom = 'unset';
    document.getElementById(currentTab).style.borderRadius =
      '10px 10px 0px 0px';
    const tabDetail =tabDetails(selectedValue);
    for (let i = 1; i <= tabDetail.length; i++) {
      if (currentTab.charAt(currentTab.length - 1) != i) {
        document.getElementById(
          SELECTED_TAB_KEY.PACSQUEUE + `${i}`
        ).style.border = '';
        document.getElementById(
          SELECTED_TAB_KEY.PACSQUEUE + `${i}`
        ).style.borderRadius = '';
        document.getElementById(
          SELECTED_TAB_KEY.PACSQUEUE + `${i}`
        ).style.borderBottom = '';
      }
    }
    setSelectedValue(data.value);
  };

  useEffect(() => {
    if (
      document.getElementById(SELECTED_TAB_KEY.PACSQUEUE + '1') != null
    ) {
      document.getElementById(
        SELECTED_TAB_KEY.PACSQUEUE + '1'
      ).style.border = getThemeColor('border');
      document.getElementById(
        SELECTED_TAB_KEY.PACSQUEUE + '1'
      ).style.borderBottom = 'unset';
      document.getElementById(
        SELECTED_TAB_KEY.PACSQUEUE + '1'
      ).style.borderRadius = '10px 10px 0px 0px';
    }
  }, [document.getElementById(SELECTED_TAB_KEY.PACSQUEUE + '1')]);

  return (
    <>
      {roles?.length > 0 && (
        <div className="main-container-body">
          <div className="breadCrumb">
            <CustomBreadCrumb items={breadCrumbItems} />
          </div> 
          <div
            style={{
              padding: '0px 3px',
              marginTop: '10px',
              boxSizing: 'border-box',
              height: 'calc(100% - 101px)',
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <PacsQueueTabSection
                onTabSelect={onTabSelect}
                selectedValue={selectedValue}
                tabDetails={tabDetails}
              />
              <div style={{ marginLeft: "auto", marginRight: "10px" }}>
                {(roles?.[0] == USERTYPES.ADMIN ||
                  roles?.[0] == USERTYPES.FIRMADMIN ||
                  roles?.[0] == USERTYPES.SUPERVISOR ||
                  roles?.[0] == USERTYPES.FIRMDOCTOR ||
                  roles?.[0] == USERTYPES.FIRMTECHNICIAN||
                roles?.[0] == USERTYPES.RADIOLOGIST) && (
                  <PrimaryButton
                    text="Export CSV"
                    className="common-default-active-button list-view-top-buttons-styles"
                    onClick={() => setShowPacsQueueExportCsvModal(true)}
                  />
                )}
              </div>
            </div>
            <div
              style={{
                borderBottom: getThemeColor('border'),
                width: '100%',
                boxSizing: 'border-box',
              }}
            ></div>
            {<PacsQueueList selectedValue={selectedValue} />}
            <PacsQueueExportCsvModal
              show={showPacsQueueExportCsvModal}
              onClose={() => setShowPacsQueueExportCsvModal(false)}
              onExportInitiated={handleExportInitiated}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default PacsQueue;

const PacsQueueTabSection = ({ onTabSelect, selectedValue, tabDetails }) => {
  const getTabActiveStatus = (tabId) => {
    if (selectedValue === tabId) return true;
    return false;
  };

  return (
    <TabList
      style={{ marginLeft: '10px' }}
      className="tab-list-container-cmp tabList"
      defaultSelectedValue={SELECTED_TAB_KEY.PACSQUEUE + '1'}
      selectedValue={selectedValue}
      onTabSelect={onTabSelect}
    >
      {tabDetails(selectedValue).map((tabElement, index) => {
        return (
          <Tab
            key={index}
            className="top-tab-cmp"
            id={SELECTED_TAB_KEY.PACSQUEUE + (index + 1).toString()}
            value={SELECTED_TAB_KEY.PACSQUEUE + (index + 1).toString()}
          >
            {
              <PacsQueueTab
                title={tabElement.title}
                icon={tabElement.icon}
                contentClass={tabElement.contentClass}
                active={getTabActiveStatus(tabElement.id)}
              />
            }
          </Tab>
        );
      })}
    </TabList>
  );
};
